import { Injector, Provider } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SpaConfig } from '@tourfold/libraries/spa-config/spa-config';
import {
  createAuthHeaderMiddleware,
  createLanguageMiddleware,
  createRefreshMiddleware,
  provideApiClientGroup,
} from '@tourfold/libraries/tf-api-clients-provider-angular';
import { USER_TOKEN_STORE, type UserTokenStore } from '@tourfold/shared-kernels/auth';
import { PlatformI18nService } from '@tourfold/shared-kernels/i18n';
import {
  createFrontendVersionMiddleware,
  FrontendVersionService,
} from '@tourfold/libraries/shared-util';
import { WEB_APP_TENANT_ID_STORAGE_KEY } from '../../app.token-store';
import {
  AIAssistantSettingsApi,
  AreasApi,
  AuditLogApi,
  BillingInvoicesApi,
  BillingPortalApi,
  BillingProfileApi,
  BrandsApi,
  CommentsApi,
  Configuration as PublicV2Configuration,
  CustomObjectDefinitionsApi,
  CustomObjectInstancesApi,
  CustomObjectRelationsApi,
  CustomObjectsGraphQLApi,
  DevicesApi,
  DocumentManagementApi,
  GeocodingSettingsApi,
  GrantsApi,
  MCPServerApi,
  MeasureUnitsApi,
  type Middleware as PublicV2Middleware,
  RolesApi,
  SkillsApi,
  SmsGatewaysApi,
  SmsReportApi,
  TagsApi,
  TenantApi,
  UsersApi,
  WebhookDeliveryFailuresApi,
  WebhookEndpointsApi,
  WebhookTestingApi,
  WebhooksCatalogApi,
} from '@tourfold/libraries/tf-api-client-public-v2';
import {
  AIAssistantApi,
  ActivitiesV2Api,
  AuthV2Api,
  BillingApi,
  BwAddressesV2Api,
  BwCasesV2Api,
  BwJobsV2Api,
  BwOfferingsV2Api,
  BwToursV2Api,
  CalendarsV2Api,
  CasesV2Api,
  CustomDomainsApi,
  DevicesV2Api,
  Configuration as FrontendV2Configuration,
  FrontendConfigurationApi,
  FrontendThemeApi,
  FrontendUserPreferencesApi,
  GeocodingV2Api,
  GeoservicesRoutingApi,
  JobsV2Api,
  type Middleware as FrontendV2Middleware,
  NotificationsApi,
  OfferingsV2Api,
  PushSettingsV2Api,
  SavedListViewsApi,
  SavedListViewsSettingsApi,
  SkillsV2Api,
  StatisticsDashboardV2Api,
  TodoTemplatesV2Api,
  ToursV2Api,
  UsageApi,
  UsageSpendLimitsControllerApi,
  VehicleTypesV2Api,
  VehiclesV2Api,
  WorkflowsApi,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import { AuthService } from '../../services/auth.service';
import { RefreshCoordinator } from '../../services/refresh-coordinator.service';

/**
 * Builds the middleware stack the web shell uses for every generated fetch-based API client:
 *
 *  - `createAuthHeaderMiddleware` reads the access token through the `USER_TOKEN_STORE` port (same
 *    abstraction `AuthService` and `RefreshCoordinator` write to, so the middleware always sees
 *    a freshly refreshed token without depending on the literal localStorage key). Tenant id is
 *    read via the shared `WEB_APP_TENANT_ID_STORAGE_KEY` constant — kept outside `USER_TOKEN_STORE`
 *    because tenants are a web-shell concept (mobile-app binds tenant differently). Skips
 *    auth on `/auth/` endpoints because Spring Security validates a stale Bearer if present;
 *    skips the tenant header on `geo.openresearch.com` because that's an external service.
 *  - `createLanguageMiddleware` pulls the current Accept-Language from `PlatformI18nService`.
 *  - `createRefreshMiddleware` coordinates a single token refresh across all in-flight callers,
 *    then retries once. On failure it hands off to `AuthService.handleSessionExpired()`, which
 *    remembers the current `/app` location (so the user is returned there after re-login) before
 *    tearing the session down.
 */
function buildWebMiddleware(
  injector: Injector,
  platformI18n: PlatformI18nService,
  versionService: FrontendVersionService
) {
  const tokenStore = injector.get<UserTokenStore>(USER_TOKEN_STORE);
  return [
    createAuthHeaderMiddleware({
      getAccessToken: (url) => (url.includes('/auth/') ? null : tokenStore.getAccessToken()),
      getTenantId: () => localStorage.getItem(WEB_APP_TENANT_ID_STORAGE_KEY),
      tenantHeaderExcludeUrls: ['geo.openresearch.com'],
    }),
    createLanguageMiddleware(() => platformI18n.resolveAcceptLanguage()),
    createRefreshMiddleware({
      coordinateRefresh: async () => {
        // RefreshCoordinator.coordinateRefresh() resolves to AuthUserV2Response (object) on
        // success and an `'error: …'` string on failure — see refresh-coordinator.service.ts.
        // typeof !== 'string' maps that contract to the boolean the middleware expects.
        const result = await firstValueFrom(injector.get(RefreshCoordinator).coordinateRefresh());
        return typeof result !== 'string';
      },
      onRefreshFailure: () => injector.get(AuthService).handleSessionExpired(),
      skipForUrlsContaining: ['/auth'],
    }),
    // TF-1459: tags every outbound request with X-Frontend-Version and reads
    // X-Minimum-Frontend-Version from the response so the banner / reload
    // orchestrator can react when the deployed bundle drops below backend's floor.
    createFrontendVersionMiddleware(versionService),
  ];
}

function publicV2ConfigurationFactory(
  injector: Injector,
  platformI18n: PlatformI18nService,
  versionService: FrontendVersionService
): PublicV2Configuration {
  return new PublicV2Configuration({
    basePath: SpaConfig.apiUrlProvider.rootUrl,
    // Generated `Middleware` is structurally identical to the kernel's `ApiClientMiddleware`
    // (same `pre`/`post` shape from the OpenAPI template). Cast is purely to satisfy the
    // nominal-type boundary between the kernel lib and the generated client.
    middleware: buildWebMiddleware(injector, platformI18n, versionService) as unknown as PublicV2Middleware[],
  });
}

function frontendV2ConfigurationFactory(
  injector: Injector,
  platformI18n: PlatformI18nService,
  versionService: FrontendVersionService
): FrontendV2Configuration {
  return new FrontendV2Configuration({
    basePath: SpaConfig.apiUrlProvider.rootUrl,
    middleware: buildWebMiddleware(injector, platformI18n, versionService) as unknown as FrontendV2Middleware[],
  });
}

/**
 * Web-app fetch-based generated API clients wired with shared middleware (auth, language,
 * refresh). Used to be ~250 lines of repetitive `{ provide, useFactory, deps }` blocks
 * inside `main.ts`; collapsed via `provideApiClientGroup` from the api-client-angular lib.
 */
export const WEB_API_CLIENT_PROVIDERS: Provider[] = [
  ...provideApiClientGroup({
    configurationToken: PublicV2Configuration,
    configurationFactory: publicV2ConfigurationFactory,
    configurationDeps: [Injector, PlatformI18nService, FrontendVersionService],
    apis: [
      AIAssistantSettingsApi,
      AreasApi,
      AuditLogApi,
      BillingInvoicesApi,
      BillingPortalApi,
      BillingProfileApi,
      BrandsApi,
      CommentsApi,
      CustomObjectDefinitionsApi,
      CustomObjectInstancesApi,
      CustomObjectRelationsApi,
      CustomObjectsGraphQLApi,
      DevicesApi,
      DocumentManagementApi,
      GeocodingSettingsApi,
      GrantsApi,
      MCPServerApi,
      MeasureUnitsApi,
      RolesApi,
      SkillsApi,
      SmsGatewaysApi,
      SmsReportApi,
      TagsApi,
      TenantApi,
      UsersApi,
      WebhookDeliveryFailuresApi,
      WebhookEndpointsApi,
      WebhookTestingApi,
      WebhooksCatalogApi,
    ],
  }),
  ...provideApiClientGroup({
    configurationToken: FrontendV2Configuration,
    configurationFactory: frontendV2ConfigurationFactory,
    configurationDeps: [Injector, PlatformI18nService, FrontendVersionService],
    apis: [
      AIAssistantApi,
      ActivitiesV2Api,
      AuthV2Api,
      BillingApi,
      BwAddressesV2Api,
      BwCasesV2Api,
      BwJobsV2Api,
      BwOfferingsV2Api,
      BwToursV2Api,
      CalendarsV2Api,
      CasesV2Api,
      CustomDomainsApi,
      DevicesV2Api,
      FrontendConfigurationApi,
      FrontendThemeApi,
      FrontendUserPreferencesApi,
      GeocodingV2Api,
      GeoservicesRoutingApi,
      JobsV2Api,
      NotificationsApi,
      OfferingsV2Api,
      PushSettingsV2Api,
      SavedListViewsApi,
      SavedListViewsSettingsApi,
      SkillsV2Api,
      StatisticsDashboardV2Api,
      TodoTemplatesV2Api,
      ToursV2Api,
      UsageApi,
      UsageSpendLimitsControllerApi,
      VehicleTypesV2Api,
      VehiclesV2Api,
      WorkflowsApi,
    ],
  }),
];
